import {
  ActionBar,
  Popover,
  useViewport,
  ViewportProvider,
  WithPopover,
} from '@lightspeed/design-system-react'
import { Button as UcButton } from '@lightspeed/unified-components-helios-theme/react'
import { T } from '@transifex/react'
import React from 'react'
import { ApiProduct } from '@vend/types'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../../models/fulfilment'
import { FormButtonOnEmptyShippingAddress } from '../../address/FormButtonOnEmptyShippingAddress'
import { LineItemFulfilmentAddCustomerView } from '../LineItemFulfilmentAddCustomerView'
import { LineItemFulfilmentCustomerDetailsView } from '../LineItemFulfilmentCustomerDetailsView'
import { LineItemFulfilmentDecisionView } from '../LineItemFulfilmentDecisionView'
import { LineItemFulfilmentDetailsView } from '../LineItemFulfilmentDetailsView'
import { LineItemFulfilmentFixSaleAddresses } from '../LineItemFulfilmentFixSaleAddresses'
import { LineItemFulfilmentNewCustomerDetailsView } from '../LineItemFulfilmentNewCustomerDetailsView'
import {
  CurrentView,
  LineItemFulfillmentProvider,
} from '../LineItemFulfillmentContext'
import {
  useLineItemFulfilmentPopoverModel,
  useShouldShowWidePopover,
} from './useLineItemFulfilmentPopoverModel'

export type LineItemFulfilmentPopoverProps = {
  children: React.ReactNode
  open: boolean
  productQuantity: number
  productName: string
  onFulfilmentDetailsSet: (fulfilmentDetails: FulfilmentDetail) => void
  onClose: () => void
  defaultView?: CurrentView
  defaultFulfilmentDetail?: FulfilmentDetail
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
  canShowBreakdownOption?: boolean
  outerProduct?: ApiProduct
  breakdownOuterPacks?: () => Promise<boolean>
  isBreakingDown?: boolean
  showSpecialOrderMessage?: boolean
}

type ComponentProps = Omit<LineItemFulfilmentPopoverProps, 'children' | 'open'>

export const TContext = 'Line item fulfilment popover'

const POPOVER_CONTENT_CLASS =
  'uc-base font-general flex max-h-[70vh] flex-col text-left'
const CUSTOMER_SEARCH_POPOVER_CLASS =
  'fulfillment-popover--customer-search-open'
const POPOVER_ACTIONS_CLASS = 'uc-base font-general'

const WithResponsivePopover: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { ExtraSmall, Small, Medium } = useViewport()

  return (
    <WithPopover
      {...(ExtraSmall || Small || Medium
        ? {
            direction: 'below' as const,
          }
        : {
            direction: 'left' as const,
            alignment: 'start' as const,
          })}
    >
      {children}
    </WithPopover>
  )
}

const LineItemFulfilmentPopoverComponent: React.FC<ComponentProps> = ({
  productQuantity,
  productName,
  onFulfilmentDetailsSet,
  onClose,
  canShowBreakdownOption,
  outerProduct,
  breakdownOuterPacks,
  isBreakingDown,
  showSpecialOrderMessage,
}: ComponentProps) => {
  const {
    currentView,
    currentDecision,
    currentCustomer,
    customerGroup,
    isEmailInvalid,
    fulfilmentDetails,
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePhoneNumberChange,
    handleSelectFulfillmentType,
    handleNoteChange,
    onAddNewCustomer,
    onSelectCustomer,
    onRemoveCustomer,
    isContinueButtonDisabled,
    onClickContinue,
    handleDecisionSelect,
    saleHasServiceOrder,
  } = useLineItemFulfilmentPopoverModel({
    onFulfilmentDetailsSet,
    onClose,
    breakdownOuterPacks,
  })
  const showWidePopover = useShouldShowWidePopover()
  const isCustomerSearchView = currentView === CurrentView.ADD_CUSTOMER

  const renderView = () => {
    switch (currentView) {
      case CurrentView.DECISION:
        return (
          <LineItemFulfilmentDecisionView
            decision={currentDecision}
            productName={productName}
            productQuantity={productQuantity}
            onSelect={handleDecisionSelect}
            canShowBreakdownOption={canShowBreakdownOption}
            outerProduct={outerProduct}
            saleHasServiceOrder={saleHasServiceOrder}
          />
        )
      case CurrentView.ADD_CUSTOMER:
        return (
          <LineItemFulfilmentAddCustomerView
            onSelectCustomer={onSelectCustomer}
            onAddNewCustomer={onAddNewCustomer}
          />
        )
      case CurrentView.CREATE_NEW_CUSTOMER:
        return (
          <LineItemFulfilmentNewCustomerDetailsView
            customer={currentCustomer}
            onFirstNameChange={handleFirstNameChange}
            onLastNameChange={handleLastNameChange}
            onEmailChange={handleEmailChange}
            onPhoneNumberChange={handlePhoneNumberChange}
            isEmailInvalid={isEmailInvalid}
          />
        )
      case CurrentView.REVIEW_CONTACT_INFO:
        return (
          currentCustomer && (
            <LineItemFulfilmentCustomerDetailsView
              onRemoveCustomer={onRemoveCustomer}
              onEmailChange={handleEmailChange}
              onPhoneNumberChange={handlePhoneNumberChange}
              isEmailInvalid={isEmailInvalid}
              customer={currentCustomer}
              customerGroup={customerGroup}
            />
          )
        )
      case CurrentView.FIX_SALE_ADDRESSES:
        return <LineItemFulfilmentFixSaleAddresses />

      default:
        return (
          <LineItemFulfilmentDetailsView
            fulfilmentType={
              isDefinedFulfilmentType(fulfilmentDetails.type)
                ? fulfilmentDetails.type
                : FulfilmentType.DELIVERY
            }
            fulfilmentNote={fulfilmentDetails.note}
            productName={productName}
            onSelect={handleSelectFulfillmentType}
            onNoteChange={handleNoteChange}
            showSpecialOrderMessage={showSpecialOrderMessage}
          />
        )
    }
  }

  const renderButtons = () => {
    if (currentView !== CurrentView.FIX_SALE_ADDRESSES) {
      return (
        <UcButton
          appearance="primary"
          customDataAttributes={{
            container: {
              'data-cy': 'continue-fulfilment-button',
              'data-track': 'continue-fulfilment',
            },
          }}
          disabled={isContinueButtonDisabled()}
          loading={isBreakingDown}
          onClick={onClickContinue}
          size="medium"
        >
          <T _str="Continue" _context={TContext} />
        </UcButton>
      )
    }

    return (
      <div className="flex w-full flex-row flex-nowrap justify-end gap-3">
        <FormButtonOnEmptyShippingAddress
          fulfilmentDetail={fulfilmentDetails}
          onClose={onClose}
        />
      </div>
    )
  }

  return (
    <Popover
      className={`${
        showWidePopover ? 'fulfillment-popover-wide' : 'fulfillment-popover'
      } ${isCustomerSearchView ? CUSTOMER_SEARCH_POPOVER_CLASS : ''}`}
      onClose={onClose}
      actions={
        <ActionBar className="vd-pt3 vd-pb3">
          <div />
          <div className={POPOVER_ACTIONS_CLASS}>{renderButtons()}</div>
        </ActionBar>
      }
    >
      <div
        className={`${POPOVER_CONTENT_CLASS} ${
          isCustomerSearchView ? 'overflow-visible' : 'overflow-hidden'
        }`}
      >
        {renderView()}
      </div>
    </Popover>
  )
}

const FulfillmentPopoverWrapper: React.FC<LineItemFulfilmentPopoverProps> = ({
  children,
  open,
  productQuantity,
  productName,
  onFulfilmentDetailsSet,
  onClose,
  onSelectContinueAnyway,
  canShowBreakdownOption = false,
  outerProduct,
  breakdownOuterPacks,
  isBreakingDown,
  showSpecialOrderMessage,
}: LineItemFulfilmentPopoverProps) => {
  return (
    <ViewportProvider>
      <WithResponsivePopover>
        {children}
        {open && (
          <LineItemFulfilmentPopoverComponent
            productQuantity={productQuantity}
            productName={productName}
            onFulfilmentDetailsSet={onFulfilmentDetailsSet}
            onClose={onClose}
            onSelectContinueAnyway={onSelectContinueAnyway}
            canShowBreakdownOption={canShowBreakdownOption}
            outerProduct={outerProduct}
            breakdownOuterPacks={breakdownOuterPacks}
            isBreakingDown={isBreakingDown}
            showSpecialOrderMessage={showSpecialOrderMessage}
          />
        )}
      </WithResponsivePopover>
    </ViewportProvider>
  )
}

export const LineItemFulfilmentPopoverMemo = React.memo(
  FulfillmentPopoverWrapper
)

export const LineItemFulfilmentPopover = (
  props: LineItemFulfilmentPopoverProps
) => (
  <LineItemFulfillmentProvider
    defaultView={props.defaultView}
    defaultFulfillmentDetail={props.defaultFulfilmentDetail}
    onSelectContinueAnyway={props.onSelectContinueAnyway}
    onSelectFulfilmentType={props.onSelectFulfilmentType}
  >
    <LineItemFulfilmentPopoverMemo {...props} />
  </LineItemFulfillmentProvider>
)
